import type { SystemStyleObject } from '@/ui';
import { PRINT_SPACING, PRINT_TYPOGRAPHY } from '../constants';

/**
 * Type definitions for print style options
 */
export type PrintStyleObject = SystemStyleObject & {
  '@media print'?: SystemStyleObject;
};

export interface ListItemOptions {
  bullet?: string;
  spacing?: string;
  fontSize?: string;
}

export interface TypographyOptions {
  fontSize?: string;
  align?: 'left' | 'center' | 'right';
  margin?: string;
  borderWidth?: string;
  display?: 'inline' | 'block' | 'inline-block';
  color?: string;
  lineHeight?: string | number;
}

export interface EventCardOptions {
  bullet?: string;
  spacing?: string;
}

export interface MonthSectionOptions {
  spacing?: string;
}

export interface PageBreakOptions {
  type?: 'avoid' | 'always' | 'auto';
}

/**
 * Core print style wrapper - wraps any styles in @media print
 */
export const print = (styles: SystemStyleObject): PrintStyleObject => ({
  '@media print': styles,
});

/**
 * Print visibility utilities
 */
export const printVisibility = {
  // Hide in print
  hide: (): PrintStyleObject =>
    print({
      display: 'none !important',
    }),

  // Show only in print
  showOnly: (styles: SystemStyleObject = {}): PrintStyleObject => ({
    display: 'none',
    '@media print': {
      display: 'block !important',
      ...styles,
    },
  }),

  // Show only in print as inline
  showInline: (styles: SystemStyleObject = {}): PrintStyleObject => ({
    display: 'none',
    '@media print': {
      display: 'inline !important',
      ...styles,
    },
  }),

  // Show only in print as flex
  showFlex: (styles: SystemStyleObject = {}): PrintStyleObject => ({
    display: 'none',
    '@media print': {
      display: 'flex !important',
      ...styles,
    },
  }),
} as const;

/**
 * Layout transformation utilities
 */
export const printLayout = {
  // Transform card to list item
  listItem: (options: ListItemOptions = {}): PrintStyleObject => {
    const { bullet = '•', spacing = '1.5rem', fontSize = '1.5em' } = options;

    return print({
      position: 'relative',
      padding: '0 !important',
      paddingLeft: `${spacing} !important`,
      background: 'none !important',
      border: 'none !important',
      boxShadow: 'none !important',
      borderRadius: '0 !important',
      breakInside: 'avoid',
      pageBreakInside: 'avoid',

      '&::before': {
        content: `"${bullet}"`,
        position: 'absolute',
        left: '0',
        top: '0',
        fontSize: fontSize,
        fontWeight: 'bold',
        color: 'black',
        lineHeight: '1.2',
      },
    });
  },

  // Remove container constraints
  fullWidth: (): PrintStyleObject =>
    print({
      maxWidth: 'none !important',
      width: '100% !important',
    }),

  // Remove all spacing
  noSpacing: (): PrintStyleObject =>
    print({
      margin: '0 !important',
      padding: '0 !important',
    }),

  // Compact spacing
  compact: (spacing: string = '0.5rem'): PrintStyleObject =>
    print({
      marginBottom: `${spacing} !important`,
      paddingTop: '0 !important',
      paddingBottom: '0 !important',
    }),

  // Convert flex to block
  blockLayout: (): PrintStyleObject =>
    print({
      display: 'block !important',
      gap: '0 !important',
      flexDirection: 'column !important',
    }),

  // Page break controls
  pageBreak: {
    avoid: (): PrintStyleObject =>
      print({
        breakInside: 'avoid',
        pageBreakInside: 'avoid',
      }),
    before: (): PrintStyleObject =>
      print({
        pageBreakBefore: 'always',
      }),
    after: (): PrintStyleObject =>
      print({
        pageBreakAfter: 'always',
      }),
  },
} as const;

/**
 * Typography utilities for print
 */
export const printTypography = {
  // Main title
  title: (options: TypographyOptions = {}): PrintStyleObject => {
    const {
      fontSize = PRINT_TYPOGRAPHY.sizes['3xl'],
      align = 'center',
      margin = 0,
    } = options;

    return print({
      fontSize: `${fontSize} !important`,
      fontWeight: 'bold !important',
      color: 'black !important',
      textAlign: align,
      margin: `${margin} !important`,
      border: 'none !important',
      padding: '0 !important',
    });
  },

  // Section headings
  heading: (options: TypographyOptions = {}): PrintStyleObject => {
    const {
      fontSize = PRINT_TYPOGRAPHY.sizes['2xl'],
      margin = `0 0 ${PRINT_SPACING.component.sm} 0`,
      borderWidth = PRINT_SPACING.list.border,
    } = options;

    return print({
      fontSize: `${fontSize} !important`,
      lineHeight: PRINT_TYPOGRAPHY.lineHeights.base,
      fontWeight: `${PRINT_TYPOGRAPHY.weights.bold} !important`,
      color: 'black !important',
      margin: `${margin} !important`,
      paddingBottom: '0.25rem !important',
      borderBottom: `${borderWidth} solid black !important`,
      pageBreakAfter: 'avoid',
    });
  },

  // Event title
  eventTitle: (options: TypographyOptions = {}): PrintStyleObject => {
    const { fontSize = PRINT_TYPOGRAPHY.sizes.lg, display = 'inline-flex' } =
      options;

    return print({
      fontSize: `${fontSize} !important`,
      fontWeight: `${PRINT_TYPOGRAPHY.weights.bold} !important`,
      color: 'black !important',
      margin: '0 !important',
      display: `${display} !important`,
      lineHeight: `${PRINT_TYPOGRAPHY.lineHeights.base} !important`,
    });
  },

  eventEmoji: (options: TypographyOptions = {}): PrintStyleObject => {
    const {
      fontSize = PRINT_TYPOGRAPHY.sizes.lg,
      margin = '0',
      display = 'inline-flex',
    } = options;

    return print({
      fontSize: `${fontSize} !important`,
      margin: `${margin} !important`,
      minWidth: '1.65em',
      display: `${display} !important`,
      lineHeight: `${PRINT_TYPOGRAPHY.lineHeights.base} !important`,

      '--experience-card-emoji1-pos-left': '0',
      '--experience-card-emoji1-font-size': `${fontSize} !important`,
      '--experience-card-emoji1-text-shadow': 'none',
      '--experience-card-emoji1-position': 'relative',
      '--experience-card-emoji2-display': 'none',
    });
  },

  // Event date
  eventDate: (options: TypographyOptions = {}): PrintStyleObject => {
    const { fontSize = '11pt', color = '#666' } = options;

    return print({
      fontSize: `${fontSize} !important`,
      color: `${color} !important`,
      fontWeight: 'normal !important',
      whiteSpace: 'nowrap',
      margin: '0 !important',
      display: 'inline !important',
    });
  },

  // Description text
  description: (options: TypographyOptions = {}): PrintStyleObject => {
    const {
      fontSize = PRINT_TYPOGRAPHY.sizes.md,
      color = '#666',
      lineHeight = PRINT_TYPOGRAPHY.lineHeights.relaxed,
      margin = `${PRINT_SPACING.component.md} 0 0 0`,
    } = options;

    return print({
      fontSize: `${fontSize} !important`,
      color: `${color} !important`,
      lineHeight: `${lineHeight} !important`,
      margin: `${margin} !important`,
    });
  },

  // Remove all font size overrides
  resetFontSize: (): PrintStyleObject =>
    print({
      fontSize: 'inherit !important',
    }),
} as const;

/**
 * Component-specific utilities
 */
export const printComponents = {
  eventMetaData: (): PrintStyleObject => {
    return print({
      display: 'flex !important',
      flexDirection: 'row !important',
      gap: '1rem !important',
      justifyContent: 'space-between !important',
      alignItems: 'center !important',
      marginTop: '0 !important',

      '& > *': {
        margin: '0 !important',
        padding: '0 !important',
      },
    });
  },

  // Heading component transformation
  heading: (): PrintStyleObject => {
    return {
      ...printLayout.noSpacing(),
      ...print({
        marginBottom: `0 !important`,
        background: 'none !important',
        backgroundColor: 'transparent !important',
        border: 'none !important',
        borderRadius: '0 !important',
        boxShadow: 'none !important',
        isolation: 'auto !important',

        // Reset your CSS variables
        '--experience-card-padding': '0 !important',

        '& button, & [role="button"]': {
          display: 'none !important',
        },
      }),
    };
  },

  // Event card transformation
  eventCard: (options: EventCardOptions = {}): PrintStyleObject => {
    const { bullet = '•', spacing = '0' } = options;

    return {
      ...printLayout.listItem({ bullet }),
      ...print({
        marginBottom: `${spacing} !important`,
        background: 'none !important',
        backgroundColor: 'transparent !important',
        border: 'none !important',
        borderRadius: '0 !important',
        boxShadow: 'none !important',
        isolation: 'auto !important',

        // Reset your CSS variables
        '--experience-card-border': 'none !important',
        '--experience-card-radius': '0 !important',
        '--experience-card-shadow1': 'none !important',
        '--experience-card-shadow2': 'none !important',

        '& button, & [role="button"]': {
          display: 'none !important',
        },
      }),
    };
  },

  // Event card transformation
  eventCardBody: (options: EventCardOptions = {}): PrintStyleObject => {
    const { spacing = '0' } = options;

    return {
      ...printLayout.noSpacing(),
      ...print({
        // Reset your CSS variables
        '--experience-block-spacing': `${spacing} !important`,
        '--experience-card-padding': '0 !important',
      }),
    };
  },

  // Event footer section
  eventCardFooter: (): PrintStyleObject =>
    print({
      display: 'none !important',
    }),

  // Month section
  monthSection: (options: MonthSectionOptions = {}): PrintStyleObject => {
    const { spacing = '2rem' } = options;

    return {
      ...printLayout.pageBreak.avoid(),
      ...print({
        marginBottom: `${spacing} !important`,
      }),
    };
  },

  // Calendar container
  calendarContainer: (): PrintStyleObject => ({
    ...printLayout.fullWidth(),
    ...printLayout.noSpacing(),
  }),
} as const;

/**
 * Pre-built style combinations for common patterns
 */
export const printPresets = {
  // Hide all interactive elements
  hideInteractive: (): PrintStyleObject =>
    print({
      'button, [role="button"], .chakra-button, input, select, textarea': {
        display: 'none !important',
      },
    }),

  // Hide all images
  hideImages: (): PrintStyleObject =>
    print({
      'img, .chakra-image, picture, svg, .chakra-icon, .chakra-avatar': {
        display: 'none !important',
      },
      '*': {
        backgroundImage: 'none !important',
      },
    }),

  // Standard page layout
  page: (): PrintStyleObject => ({
    ...printPresets.hideInteractive(),
    ...printPresets.hideImages(),
  }),

  // Standard calendar layout
  calendarLayout: (): PrintStyleObject => ({
    ...printPresets.hideInteractive(),
    ...printPresets.hideImages(),
    ...print({
      // Remove all backgrounds
      '*': {
        background: 'white !important',
        backgroundColor: 'white !important',
        backgroundImage: 'none !important',
      },
    }),
  }),

  // Compact calendar (more content per page)
  compactCalendar: (): PrintStyleObject => ({
    ...printPresets.calendarLayout(),
    ...print({
      // Tighter spacing
      '*': {
        lineHeight: '1.2 !important',
      },
    }),
  }),
} as const;

/**
 * Advanced utility for combining multiple print styles
 */
export const combinePrintStyles = (
  ...styleObjects: PrintStyleObject[]
): PrintStyleObject => {
  const combined: PrintStyleObject = {};

  styleObjects.forEach((styleObj) => {
    if (styleObj['@media print']) {
      if (!combined['@media print']) {
        combined['@media print'] = {};
      }
      Object.assign(combined['@media print'], styleObj['@media print']);
    } else {
      Object.assign(combined, styleObj);
    }
  });

  return combined;
};

/**
 * Type for the print styles hook return value
 */
export interface PrintStylesHook {
  print: typeof print;
  visibility: typeof printVisibility;
  layout: typeof printLayout;
  typography: typeof printTypography;
  components: typeof printComponents;
  presets: typeof printPresets;
  combine: typeof combinePrintStyles;
}

/**
 * Custom hook for print styles
 */
export const usePrintStyles = (): PrintStylesHook => {
  return {
    print,
    visibility: printVisibility,
    layout: printLayout,
    typography: printTypography,
    components: printComponents,
    presets: printPresets,
    combine: combinePrintStyles,
  };
};

/**
 * Type guards and utility types
 */
export type PrintStyleFunction<T = object> = (options?: T) => PrintStyleObject;

export type PrintVisibilityMethods = typeof printVisibility;
export type PrintLayoutMethods = typeof printLayout;
export type PrintTypographyMethods = typeof printTypography;
export type PrintComponentMethods = typeof printComponents;
export type PrintPresetMethods = typeof printPresets;

export type PrintStylesMethods = PrintVisibilityMethods &
  PrintLayoutMethods &
  PrintTypographyMethods &
  PrintComponentMethods &
  PrintPresetMethods;
